import { useEffect, useRef, useCallback } from 'react';
import { Div } from './HeroBgAnimationStyles';

const ParallaxParticles = () => {
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const isVisibleRef = useRef(true);
  const dimensionsRef = useRef({ width: 0, height: 0 });
  const resizeObserverRef = useRef(null);
  const particlesRef = useRef([]);
  const ctxRef = useRef(null);

  const setCanvasSize = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = ctxRef.current;
    if (!canvas || !ctx) return;

    const { width, height } = dimensionsRef.current;
    const dpr = window.devicePixelRatio || 1;

    canvas.width = width * dpr;
    canvas.height = height * dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);
    ctx.imageSmoothingEnabled = true;
  }, []);

  const updateDimensions = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const newWidth = canvas.offsetWidth;
    const newHeight = canvas.offsetHeight;

    if (
      newWidth !== dimensionsRef.current.width ||
      newHeight !== dimensionsRef.current.height
    ) {
      dimensionsRef.current = { width: newWidth, height: newHeight };

      const getSettings = () => {
        const w = window.innerWidth;
        let layers = 3;
        let particlesPerLayer = 20;
        if (w < 768) particlesPerLayer = 10;
        else if (w < 1200) particlesPerLayer = 15;
        return { layers, particlesPerLayer };
      };

      const { layers, particlesPerLayer } = getSettings();
      const particles = particlesRef.current;

      particles.length = 0;
      for (let l = 0; l < layers; l++) {
        for (let i = 0; i < particlesPerLayer; i++) {
          particles.push({
            x: Math.random() * newWidth,
            y: Math.random() * newHeight,
            vx: (Math.random() - 0.5) * (0.2 + l * 0.2),
            vy: (Math.random() - 0.5) * (0.2 + l * 0.2),
            size: 1 + Math.random() * 2 + l,
            alpha: 0.1 + Math.random() * 0.5,
          });
        }
      }

      setCanvasSize();
    }
  }, [setCanvasSize]);

  const animate = useCallback(() => {
    if (!isVisibleRef.current) {
      animationFrameRef.current = null;
      return;
    }

    const ctx = ctxRef.current;
    const particles = particlesRef.current;
    if (!ctx) return;

    const { width, height } = dimensionsRef.current;

    ctx.clearRect(0, 0, width, height);
    particles.forEach((p) => {
      p.x += p.vx;
      p.y += p.vy;
      if (p.x < 0 || p.x > width) p.vx *= -1;
      if (p.y < 0 || p.y > height) p.vy *= -1;

      ctx.fillStyle = `rgba(5,150,105,${p.alpha})`;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fill();
    });
    animationFrameRef.current = requestAnimationFrame(animate);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    if (ctxRef.current) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctxRef.current = ctx;
    particlesRef.current = [];

    updateDimensions();
    animate();

    resizeObserverRef.current = new ResizeObserver(() => {
      requestAnimationFrame(updateDimensions);
    });
    resizeObserverRef.current.observe(canvas);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isVisibleRef.current = entry.isIntersecting;

          if (entry.isIntersecting) {
            if (!animationFrameRef.current) {
              animate();
            }
          } else {
            if (animationFrameRef.current) {
              cancelAnimationFrame(animationFrameRef.current);
              animationFrameRef.current = null;
            }
          }
        });
      },
      {
        threshold: 0.1,
      }
    );

    observer.observe(canvas);

    return () => {
      observer.disconnect();
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      ctxRef.current = null;
    };
  }, [updateDimensions, animate]);

  return (
    <Div
      style={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <canvas
        ref={canvasRef}
        data-hero-canvas="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 'var(--z-base)',
        }}
      />
    </Div>
  );
};

export default ParallaxParticles;
